import { motion } from 'framer-motion';
import {
  SiAngular,
  SiJavascript,
  SiTypescript,
  SiHtml5,
  SiCss3,
  SiReact,
  SiPhp,
  SiMysql,
  SiGit,
  SiDocker,
  SiGitlab,
  SiAmazon,
  SiLinux,
  SiMacos,
  SiNodedotjs,
  SiPlesk,
} from 'react-icons/si';
import { FaCode } from 'react-icons/fa';
import { TbApi } from 'react-icons/tb';
import { createElement } from 'react';

interface Skill {
  name: string;
  icon: any;
  category: 'frontend' | 'backend' | 'tools';
}

const skills: Skill[] = [
  // Frontend
  { name: 'Angular', icon: SiAngular, category: 'frontend' },
  { name: 'KnockoutJS', icon: FaCode, category: 'frontend' },
  { name: 'JavaScript', icon: SiJavascript, category: 'frontend' },
  { name: 'TypeScript', icon: SiTypescript, category: 'frontend' },
  { name: 'HTML5', icon: SiHtml5, category: 'frontend' },
  { name: 'CSS3', icon: SiCss3, category: 'frontend' },
  { name: 'React', icon: SiReact, category: 'frontend' },

  // Backend
  { name: 'PHP', icon: SiPhp, category: 'backend' },
  { name: 'MySQL', icon: SiMysql, category: 'backend' },
  { name: 'API REST', icon: TbApi, category: 'backend' },
  { name: 'Node.js', icon: SiNodedotjs, category: 'backend' },

  // Outils
  { name: 'Git', icon: SiGit, category: 'tools' },
  { name: 'Docker', icon: SiDocker, category: 'tools' },
  { name: 'GitLab', icon: SiGitlab, category: 'tools' },
  { name: 'AWS', icon: SiAmazon, category: 'tools' },
  { name: 'Linux/macOS', icon: SiLinux, category: 'tools' },
  { name: 'Plesk', icon: SiPlesk, category: 'tools' },
];

const container = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

export const Skills = () => {
  const categories = {
    frontend: skills.filter((skill) => skill.category === 'frontend'),
    backend: skills.filter((skill) => skill.category === 'backend'),
    tools: skills.filter((skill) => skill.category === 'tools'),
  };

  return (
    <div className="py-12 bg-gray-50/90 dark:bg-gray-900/90">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
            Mes Compétences
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-300">
            Une vue d'ensemble de mes compétences techniques
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Frontend */}
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm p-6 rounded-lg shadow-lg border border-gray-200/50 dark:border-gray-700/50"
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              Frontend
            </h3>
            <div className="grid grid-cols-3 gap-6">
              {categories.frontend.map((skill) => (
                <motion.div
                  key={skill.name}
                  variants={item}
                  className="flex flex-col items-center justify-center group"
                >
                  <div className="w-12 h-12 flex items-center justify-center text-gray-600 dark:text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors duration-200">
                    {createElement(skill.icon, { 
                      size: 40,
                      className: "text-current" 
                    })}
                  </div>
                  <span className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-400 group-hover:text-indigo-600 dark:group-hover:text-indigo-400 transition-colors duration-200">
                    {skill.name}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Backend */}
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm p-6 rounded-lg shadow-lg border border-gray-200/50 dark:border-gray-700/50"
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              Backend
            </h3>
            <div className="grid grid-cols-3 gap-6">
              {categories.backend.map((skill) => (
                <motion.div
                  key={skill.name}
                  variants={item}
                  className="flex flex-col items-center justify-center group"
                >
                  <div className="w-12 h-12 flex items-center justify-center text-gray-600 dark:text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors duration-200">
                    {createElement(skill.icon, { size: 40 })}
                  </div>
                  <span className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-400 group-hover:text-green-600 dark:group-hover:text-green-400 transition-colors duration-200">
                    {skill.name}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Outils */}
          <motion.div
            variants={container}
            initial="hidden"
            animate="show"
            className="bg-white/95 dark:bg-gray-800/95 backdrop-blur-sm p-6 rounded-lg shadow-lg border border-gray-200/50 dark:border-gray-700/50"
          >
            <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              Outils
            </h3>
            <div className="grid grid-cols-3 gap-6">
              {categories.tools.map((skill) => (
                <motion.div
                  key={skill.name}
                  variants={item}
                  className="flex flex-col items-center justify-center group"
                >
                  <div className="w-12 h-12 flex items-center justify-center text-gray-600 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-200">
                    {createElement(skill.icon, { size: 40 })}
                  </div>
                  <span className="mt-2 text-sm font-medium text-gray-600 dark:text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400 transition-colors duration-200">
                    {skill.name}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}; 